import { cachedInflightRequest } from '@nova/core';

/**
 * Generate a cache key from method, URL, and config
 * Note: Only used for GET requests (mutating methods shouldn't be cached)
 * @param {string} method - HTTP method (should be 'get')
 * @param {string} url - Request URL
 * @param {import('axios').AxiosRequestConfig} config - Axios config
 * @returns {string} Cache key
 */
function generateCacheKey(method, url, config = {}) {
  const parts = [method.toLowerCase(), url];

  const params = config.params || {};
  if (Object.keys(params).length > 0) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});
    parts.push(`params:${JSON.stringify(sortedParams)}`);
  }

  return parts.join(':');
}

/**
 * Cacheable GET request with optional caching (caching enabled by default for GET)
 * @template T
 * @param {string} url - Request URL
 * @param {import('axios').AxiosRequestConfig & { enableCache?: boolean }} [config] - Axios config with optional enableCache (default: true for GET)
 * @returns {Promise<T>}
 */
export function cacheableGet(url, config = {}) {
  const { enableCache = true, ...axiosConfig } = config;

  const fetcher = () => axios.get(url, axiosConfig);

  /**
   * Skip cache if:
   * 1. enableCache is false
   * 2. AbortSignal is provided (requires proper cancellation handling)
   */
  if (!enableCache || axiosConfig.signal) {
    return fetcher();
  }

  const cacheKey = generateCacheKey('get', url, axiosConfig);
  return cachedInflightRequest(cacheKey, fetcher);
}
